import React, { useCallback } from 'react';
import { useGizmoStore } from '@/features/preview/stores/gizmo-store';
import type { TextItem } from '@/types/timeline';
import { loadFont, FONT_WEIGHT_MAP } from '../utils/fonts';

function measureBrowserTextWidth(
  text: string,
  fontSize: number,
  fontFamily: string,
  fontWeight: number,
  letterSpacing: number,
): number {
  if (typeof document === 'undefined') {
    return 0;
  }

  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return 0;
  }

  ctx.font = `${fontWeight} ${fontSize}px "${fontFamily}", sans-serif`;
  const baseWidth = ctx.measureText(text).width;
  const spacingWidth = Math.max(0, text.length - 1) * letterSpacing;
  return baseWidth + spacingWidth;
}

function shouldWrapCaption(
  text: string,
  fontSize: number,
  availableWidth: number,
  fontFamily: string,
  fontWeight: number,
  letterSpacing: number,
): boolean {
  if (!text || availableWidth <= 0) {
    return false;
  }

  return measureBrowserTextWidth(text, fontSize, fontFamily, fontWeight, letterSpacing) > availableWidth;
}

function truncateTextWithEllipsis(
  text: string,
  availableWidth: number,
  fontSize: number,
  fontFamily: string,
  fontWeight: number,
  letterSpacing: number,
): string {
  if (!text || availableWidth <= 0) return text;

  const normalized = text.replace(/\n/g, ' ').trim();
  if (measureBrowserTextWidth(normalized, fontSize, fontFamily, fontWeight, letterSpacing) <= availableWidth) {
    return normalized;
  }

  const ellipsis = '...';
  let low = 0;
  let high = normalized.length;
  let best = ellipsis;

  while (low <= high) {
    const mid = Math.floor((low + high) / 2);
    const candidate = `${normalized.slice(0, mid).trimEnd()}${ellipsis}`;
    const width = measureBrowserTextWidth(candidate, fontSize, fontFamily, fontWeight, letterSpacing);
    if (width <= availableWidth) {
      best = candidate;
      low = mid + 1;
    } else {
      high = mid - 1;
    }
  }

  return best;
}

/**
 * Text content with live property preview support.
 * Reads preview values from gizmo store for real-time updates during slider/picker drag.
 */
export const TextContent: React.FC<{ item: TextItem }> = ({ item }) => {
  // Read preview values from unified preview system
  const itemPreview = useGizmoStore(
    useCallback((s) => s.preview?.[item.id], [item.id])
  );
  const preview = itemPreview?.properties;

  // Use preview values if available, otherwise use item's stored values
  const letterSpacing = preview?.letterSpacing ?? item.letterSpacing ?? 0;
  const lineHeight = preview?.lineHeight ?? item.lineHeight ?? 1.2;
  const color = preview?.color ?? item.color;
  const isCaption = item.role === 'caption';
  const isHeadline = item.role === 'headline';
  const isTitleTrack = item.label === 'Title1' || item.label === 'Title2';
  const isSingleLineTemplateText = isHeadline || (isTitleTrack && item.role !== 'channel-label');

  // Load the Google Font and get the CSS fontFamily value
  // loadFont() blocks rendering until the font is ready (works for both preview and server render)
  const fontName = item.fontFamily ?? 'Inter';
  const fontFamily = loadFont(fontName);
  const isMyeongjo = (item.fontFamily ?? '').toLowerCase().includes('nanum myeongjo');

  // Get font weight from shared map
  const fontWeight = FONT_WEIGHT_MAP[item.fontWeight ?? 'normal'] ?? 400;
  const baseFontSize = preview?.fontSize ?? item.fontSize ?? 60;
  const availableWidth = Math.max(80, (item.transform?.width ?? 0) - 32);
  const fontSize = baseFontSize;
  const wrapCaption = isCaption
    ? shouldWrapCaption(item.text ?? '', fontSize, availableWidth, fontFamily, fontWeight, letterSpacing)
    : false;
  const displayText = isSingleLineTemplateText
    ? truncateTextWithEllipsis(item.text ?? '', availableWidth, fontSize, fontFamily, fontWeight, letterSpacing)
    : (item.text ?? '');

  // Map text align to flexbox justify-content (horizontal)
  const textAlignMap: Record<string, string> = {
    left: 'flex-start',
    center: 'center',
    right: 'flex-end',
  };
  const justifyContent = textAlignMap[item.textAlign ?? 'center'] ?? 'center';

  // Map vertical align to flexbox align-items
  const verticalAlignMap: Record<string, string> = {
    top: 'flex-start',
    middle: 'center',
    bottom: 'flex-end',
  };
  const alignItems = verticalAlignMap[item.verticalAlign ?? 'middle'] ?? 'center';

  // Build text shadow CSS if present
  const textShadow = item.textShadow
    ? `${item.textShadow.offsetX}px ${item.textShadow.offsetY}px ${item.textShadow.blur}px ${item.textShadow.color}`
    : undefined;

  const finalTextShadow = textShadow || undefined;

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems,
        justifyContent,
        padding: '16px',
        backgroundColor: item.backgroundColor,
        boxSizing: 'border-box',
      }}
    >
      <div
        style={{
          fontSize,
          // Use the fontFamily returned by loadFont (includes proper CSS value)
          fontFamily: fontFamily,
          fontWeight,
          fontStyle: item.fontStyle ?? 'normal',
          color,
          textAlign: item.textAlign ?? 'center',
          lineHeight,
          letterSpacing,
          textShadow: finalTextShadow,
          WebkitTextStroke: item.stroke
            ? `${isMyeongjo ? Math.max(1, Math.round(item.stroke.width * 0.65)) : item.stroke.width}px ${item.stroke.color}`
            : undefined,
          paintOrder: item.stroke ? ('stroke fill' as const) : undefined,
          textRendering: 'geometricPrecision',
          WebkitFontSmoothing: 'antialiased',
          display: 'block',
          width: '100%',
          whiteSpace: isSingleLineTemplateText ? 'nowrap' : (isCaption ? (wrapCaption ? 'normal' : 'nowrap') : 'pre-wrap'),
          overflow: 'hidden',
          textOverflow: isSingleLineTemplateText ? 'ellipsis' : 'clip',
          overflowWrap: isSingleLineTemplateText ? 'normal' : (isCaption ? (wrapCaption ? 'anywhere' : 'normal') : 'anywhere'),
          wordBreak: isSingleLineTemplateText ? 'keep-all' : (isCaption ? (wrapCaption ? 'keep-all' : 'keep-all') : 'break-word'),
          lineBreak: isCaption ? (wrapCaption ? 'auto' : 'strict') : 'auto',
        }}
      >
        {displayText}
      </div>
    </div>
  );
};
